import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";

import { Box, Typography, Paper, useTheme } from "@mui/material";
import ReactApexChart from "react-apexcharts";

export default function Speed({ series, footerText, leftText }) {
  // Acceder al tema actual de MUI
  const theme = useTheme();

  // Extraer los valores y fechas de la serie para configurar el gráfico
  const chartData = series?.[0]?.data || [];

  // Extraer solo los valores numéricos para el gráfico
  const values = chartData.map((item) =>
    typeof item === "object" && item !== null ? item.y : item
  );

  // Calcular el valor máximo de la serie
  const maxValue = Math.max(...values);

  // Calcular el valor máximo para la escala Y (redondeado hacia arriba para tener un número "limpio")
  const calculateYMax = () => {
    // Si es un porcentaje y el máximo es cercano a 100, usar 100 como máximo
    if (leftText === "Porcentaje" && maxValue <= 100) {
      return 100;
    }

    // Para otros casos, calcular un máximo apropiado
    const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
    return Math.ceil(maxValue / magnitude) * magnitude;
  };

  const yAxisMax = calculateYMax();

  // Usar colores del tema de MUI
  const primaryColor = theme.palette.primary.main;
  const textColor = theme.palette.text.primary;
  const secondaryTextColor = theme.palette.text.secondary;
  const paperBackgroundColor = theme.palette.background.paper;
  const gridColor = theme.palette.divider;
  const tooltipBackgroundColor =
    theme.palette.mode === "dark"
      ? theme.palette.grey[800]
      : theme.palette.grey[900];
  const tooltipTextColor =
    theme.palette.mode === "dark"
      ? theme.palette.common.white
      : theme.palette.common.white;

  // Configuración del gráfico
  const chartOptions = {
    chart: {
      type: "bar",
      height: 500, // Altura fija en píxeles
      toolbar: {
        show: false,
      },
      fontFamily: theme.typography.fontFamily,
      foreColor: secondaryTextColor, // Color de texto general
      background: "transparent",
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "70%",
        endingShape: "flat",
        borderRadius: Number.parseInt(theme.shape.borderRadius) / 2, // Bordes redondeados como en MUI
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ["transparent"],
    },
    colors: [primaryColor], // Usar color primario del tema
    xaxis: {
      categories: Array.from({ length: values.length }, (_, i) =>
        (i + 1).toString()
      ),
      title: {
        text: "Evaluación/Test",
        style: {
          fontSize: theme.typography.subtitle2.fontSize,
          fontWeight: theme.typography.subtitle2.fontWeight,
          color: secondaryTextColor,
        },
      },
      tickAmount: values.length > 35 ? 35 : values.length,
      labels: {
        rotate: 0,
        style: {
          fontSize: theme.typography.caption.fontSize,
          colors: secondaryTextColor,
        },
      },
      axisBorder: {
        show: true,
        color: gridColor,
      },
      axisTicks: {
        show: true,
        color: gridColor,
      },
    },
    yaxis: {
      title: {
        text: leftText || "Palabras por minuto",
        style: {
          fontSize: theme.typography.subtitle2.fontSize,
          fontWeight: theme.typography.subtitle2.fontWeight,
          color: secondaryTextColor,
        },
      },
      min: 0,
      max: yAxisMax,
      tickAmount: 10,
      labels: {
        formatter: (val) => val.toFixed(0),
        style: {
          colors: secondaryTextColor,
        },
      },
    },
    fill: {
      opacity: 1,
    },
    grid: {
      borderColor: gridColor,
      row: {
        colors: ["transparent"],
        opacity: 0.5,
      },
      xaxis: {
        lines: {
          show: true,
        },
      },
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "right",
      floating: true,
      offsetY: -25,
      offsetX: -5,
      labels: {
        colors: textColor,
      },
    },
    tooltip: {
      enabled: true,
      shared: false,
      intersect: true,
      custom: ({ series, seriesIndex, dataPointIndex, w }) => {
        // Obtener el punto de datos actual (puede ser un objeto o un valor simple)
        const dataPoint = chartData[dataPointIndex];

        // Extraer el valor y la fecha
        let value, date;

        if (typeof dataPoint === "object" && dataPoint !== null) {
          value = dataPoint.y;
          date = dataPoint.x || "";
        } else {
          value = dataPoint;
          date = "";
        }
        // Usar el número de evaluación
        const evaluationNumber = "Test " + (dataPointIndex + 1);
        return `
          <div style="
            background: ${tooltipBackgroundColor};
            color: ${tooltipTextColor};
            padding: 8px 12px;
            border-radius: ${theme.shape.borderRadius}px;
            box-shadow: ${theme.shadows[3]};
            min-width: 150px;
            font-family: ${theme.typography.fontFamily};
          ">
            <div style="font-weight: ${
              theme.typography.fontWeightBold
            }; margin-bottom: 5px; font-size: ${
          theme.typography.subtitle2.fontSize
        };">
              ${evaluationNumber}
            </div>
            <div style="display: flex; align-items: center; margin-bottom: 5px;">
              <div style="
                width: 12px;
                height: 12px;
                background-color: ${primaryColor};
                margin-right: 8px;
                border-radius: 2px;
              "></div>
              <span>${series?.[0]?.name || "Velocidad"}: ${value} ${
          leftText === "Porcentaje" ? "%" : "p/m."
        }</span>
            </div>
            ${
              date
                ? `<div style="font-size: ${theme.typography.caption.fontSize};">Fecha: ${date}</div>`
                : ""
            }
          </div>
        `;
      },
    },
    states: {
      hover: {
        filter: {
          type: "darken",
          value: 0.9,
        },
      },
      active: {
        filter: {
          type: "darken",
          value: 0.85,
        },
      },
    },
    theme: {
      mode: theme.palette.mode, // Usar modo claro/oscuro del tema
    },
  };

  // Preparar los datos para ApexCharts
  const chartSeries = [
    {
      name: series?.[0]?.name || "Velocidad",
      data: values,
    },
  ];

  return (
    <Paper
      elevation={1}
      sx={{
        p: 3,
        borderRadius: theme.shape.borderRadius + "px",
      }}
    >
      <Box sx={{ mb: 2, display: "flex", justifyContent: "flex-end" }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              width: 16,
              height: 16,
              backgroundColor: primaryColor,
              mr: 1,
              borderRadius: "2px",
            }}
          />
          <Typography variant="body1" color="text.secondary">
            {series?.[0]?.name || "Velocidad"}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ height: "500px" }}>
        <ReactApexChart
          options={chartOptions}
          series={chartSeries}
          type="bar"
          height="500"
          width="100%"
        />
      </Box>
    </Paper>
  );
}
